import React, { useState } from 'react';
import { X, Upload, Loader, Navigation } from 'lucide-react';
import { trackFood, trackError } from '../Utils/analytics';
import { MY_KITCHEN_URL, CREATE_FOOD_URL } from '../../config/api';
import { authenticatedFetch } from '../Utils/apiHelpers';
import { getCurrentLocationDetails } from '../Utils/location';
import { useImagePreviews } from '../Utils/useImagePreviews';
import '../SellFoodModal/SellFoodModal.css';

interface SellFoodModalProps {
  onClose: () => void;
}

interface FoodItem {
  food_name: string;
  description: string;
  price: string;
  category: string;
  phone_number: string;
  pickup_location: string;
  serving_size: string;
  preparation_time: string;
  available_quantity: string;
}

const SellFoodModal: React.FC<SellFoodModalProps> = ({ onClose }) => {
  const [foodItem, setFoodItem] = useState<FoodItem>({
    food_name: '',
    description: '',
    price: '',
    category: '',
    phone_number: '',
    pickup_location: '',
    serving_size: '',
    preparation_time: '',
    available_quantity: ''
  });

  const { previews, addFiles, removeAt, clearAll } = useImagePreviews();

  const [errors, setErrors] = useState<{[key: string]: string}>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [createdFoodId, setCreatedFoodId] = useState<number | null>(null);
  const [detectingLocation, setDetectingLocation] = useState(false);

  const categories = [
    { label: 'Vegetarian', value: 'veg' },
    { label: 'Non-Vegetarian', value: 'non-veg' },
  ];

  const handleInputChange = (field: keyof FoodItem, value: string) => {
    setFoodItem(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => {
        const n = { ...prev };
        delete n[field];
        return n;
      });
    }
  };

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    const validFiles = files.filter(file => {
      if (!validTypes.includes(file.type)) {
        setErrors(prev => ({ ...prev, images: 'Only JPG, PNG, and WebP images are allowed' }));
        return false;
      }
      if (file.size > 5 * 1024 * 1024) {
        setErrors(prev => ({ ...prev, images: 'Each image must be less than 5MB' }));
        return false;
      }
      return true;
    });

    if (validFiles.length === 0) { e.target.value = ''; return; }

    if (previews.length + validFiles.length > 5) {
      setErrors(prev => ({ ...prev, images: 'Maximum 5 images allowed' }));
      e.target.value = '';
      return;
    }

    addFiles(validFiles);
    if (errors.images) {
      setErrors(prev => { const n = { ...prev }; delete n.images; return n; });
    }
    e.target.value = '';
  };

  const removeImage = (index: number) => {
    removeAt(index);
  };

  const handleDetectPickupLocation = async () => {
    setDetectingLocation(true);
    try {
      const result = await getCurrentLocationDetails();
      if ('type' in result) return;
      handleInputChange('pickup_location', result.full_address || result.city);
    } catch {
      // non-critical
    } finally {
      setDetectingLocation(false);
    }
  };

  const validateForm = (): boolean => {
    const newErrors: {[key: string]: string} = {};

    if (!foodItem.food_name.trim()) newErrors.food_name = 'Food name is required';
    if (!foodItem.description.trim()) newErrors.description = 'Description is required';
    if (!foodItem.price.trim()) newErrors.price = 'Price is required';
    if (!foodItem.category) newErrors.category = 'Category is required';
    if (!foodItem.phone_number.trim()) newErrors.phone_number = 'Phone number is required';
    if (!foodItem.pickup_location.trim()) newErrors.pickup_location = 'Pickup location is required';
    if (!foodItem.serving_size.trim()) newErrors.serving_size = 'Serving size is required';
    if (!foodItem.preparation_time.trim()) newErrors.preparation_time = 'Preparation time is required';
    if (!foodItem.available_quantity.trim()) newErrors.available_quantity = 'Available quantity is required';
    if (previews.length === 0) newErrors.images = 'At least one image is required';

    if (foodItem.food_name.length > 300) newErrors.food_name = 'Food name must be less than 300 characters';
    if (foodItem.description.length > 300) newErrors.description = 'Description must be less than 300 characters';

    if (foodItem.price) {
      const priceNum = Number(foodItem.price);
      if (isNaN(priceNum)) newErrors.price = 'Price must be a valid number';
      else if (priceNum <= 0) newErrors.price = 'Price must be greater than 0';
    }

    if (foodItem.phone_number) {
      const cleanPhone = foodItem.phone_number.replace(/\s/g, '');
      if (!/^[+]?[0-9\s\-()]{7,}$/.test(cleanPhone)) newErrors.phone_number = 'Please enter a valid UK phone number';
    }

    if (foodItem.available_quantity) {
      const quantity = Number(foodItem.available_quantity);
      if (isNaN(quantity) || quantity <= 0 || !Number.isInteger(quantity)) {
        newErrors.available_quantity = 'Available quantity must be a positive whole number';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      const kitchenResponse = await authenticatedFetch(MY_KITCHEN_URL, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      });

      if (!kitchenResponse.ok && kitchenResponse.status === 404) {
        setErrors({ general: 'You need to create a kitchen first before listing food items. Please go to your profile and create a kitchen.' });
        trackError('food_listing_no_kitchen', 'sell_food_modal');
        return;
      }

      if (kitchenResponse.ok) {
        const kitchenData = await kitchenResponse.json();

        if (kitchenData.status === false && kitchenData.is_approved === true) {
          setErrors({ general: 'Your kitchen is currently blocked by the administrator. You cannot add new food items until it is unblocked.' });
          trackError('food_listing_kitchen_blocked', 'sell_food_modal');
          return;
        }

        if (kitchenData.is_approved === false) {
          if (kitchenData.rejection_reason) {
            setErrors({ general: `Your kitchen was rejected: ${kitchenData.rejection_reason}. Please recreate your kitchen and resubmit for approval.` });
          } else {
            setErrors({ general: 'Your kitchen is pending admin approval. You can list food once it is approved.' });
          }
          trackError('food_listing_kitchen_not_approved', 'sell_food_modal');
          return;
        }
      }
    } catch (error) {
      console.error('Kitchen check error:', error);
    }

    if (!validateForm()) {
      trackError('food_listing_validation_failed', 'sell_food_modal');
      return;
    }

    setIsSubmitting(true);
    setErrors({});

    try {
      const formData = new FormData();
      formData.append('food_name', foodItem.food_name.trim());
      formData.append('description', foodItem.description.trim());
      formData.append('price', foodItem.price);
      formData.append('category', foodItem.category);
      formData.append('phone_number', foodItem.phone_number.replace(/\s/g, ''));
      formData.append('pickup_location', foodItem.pickup_location.trim());
      formData.append('serving_size', foodItem.serving_size.trim());
      formData.append('preparation_time', foodItem.preparation_time.trim());
      formData.append('available_quantity', foodItem.available_quantity);

      previews.forEach(item => {
        formData.append('images', item.file);
      });

      const response = await authenticatedFetch(CREATE_FOOD_URL, {
        method: 'POST',
        body: formData,
      });

      const data = await response.json();

      if (response.ok) {
        setCreatedFoodId(data.food_id);
        setShowSuccessMessage(true);

        window.dispatchEvent(new CustomEvent('foodItemCreated', {
          detail: {
            foodId: data.food_id,
            foodName: foodItem.food_name,
            kitchenId: data.kitchen_id || null
          }
        }));

        trackFood('food_listed', foodItem.food_name);
      } else {
        let errorMessage = 'Failed to create food item';
        switch (response.status) {
          case 400:
            if (data.message && data.message.toLowerCase().includes('kitchen')) {
              errorMessage = 'You need to create a kitchen first. Please go to your profile and set up your kitchen.';
            } else {
              errorMessage = data.message || 'Invalid data provided. Please check all fields.';
            }
            break;
          case 415:
            errorMessage = 'File upload error. Please try again with different images.';
            break;
          case 500:
            errorMessage = 'Server error. Please try again later.';
            break;
          default:
            errorMessage = data.message || `Error ${response.status}: Please try again.`;
        }
        setErrors({ general: errorMessage });
        trackError('food_listing_api_failed', 'sell_food_modal');
      }
    } catch (error) {
      console.error('Food listing error:', error);
      setErrors({ general: 'Network error. Please check your connection and try again.' });
      trackError('food_listing_network_error', 'sell_food_modal');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (showSuccessMessage) {
    return (
      <div className="sell-food-overlay">
        <div className="sell-food-success-modal" onClick={e => e.stopPropagation()}>
          <div className="success-icon-container">
            <div className="success-icon">
              <svg width="60" height="60" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                <polyline points="22 4 12 14.01 9 11.01"></polyline>
              </svg>
            </div>
          </div>

          <h2 className="success-title">Food Item Created Successfully!</h2>
          <p className="success-message">Your delicious dish has been added to the platform and is now available for customers to order.</p>

          <div className="success-details">
            <div className="success-detail-item">
              <span className="detail-label">Food ID:</span>
              <span className="detail-value">{createdFoodId}</span>
            </div>
            <div className="success-detail-item">
              <span className="detail-label">Name:</span>
              <span className="detail-value">{foodItem.food_name}</span>
            </div>
            <div className="success-detail-item">
              <span className="detail-label">Price:</span>
              <span className="detail-value">£{foodItem.price}</span>
            </div>
          </div>

          <div className="success-actions">
            <button className="success-close-btn" onClick={() => { clearAll(); onClose(); }}>Close</button>
            <button className="success-add-another-btn" onClick={() => {
              setShowSuccessMessage(false);
              clearAll();
              setFoodItem({
                food_name: '',
                description: '',
                price: '',
                category: '',
                phone_number: '',
                pickup_location: '',
                serving_size: '',
                preparation_time: '',
                available_quantity: ''
              });
            }}>Add Another Item</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="sell-food-overlay">
      <div className="sell-food-modal" onClick={e => e.stopPropagation()}>
        <button className="sell-food-close-button" onClick={() => { clearAll(); onClose(); }}>
          <X size={24} />
        </button>

        <h2 className="modal-title">Sell Your Food</h2>

        <form className="sell-food-form" onSubmit={handleSubmit}>
          {/* Basic Information */}
          <div className="form-section">
            <h3>Basic Information</h3>

            <div className="form-group">
              <label htmlFor="food_name">Food Name *</label>
              <input
                id="food_name"
                type="text"
                value={foodItem.food_name}
                onChange={(e) => handleInputChange('food_name', e.target.value)}
                className={errors.food_name ? 'error' : ''}
                placeholder="e.g., Chicken Biryani"
                maxLength={300}
              />
              {errors.food_name && <div className="error-message">{errors.food_name}</div>}
            </div>

            <div className="form-group">
              <label htmlFor="description">Description *</label>
              <textarea
                id="description"
                value={foodItem.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                className={errors.description ? 'error' : ''}
                placeholder="Spicy homemade biryani with raita and traditional spices..."
                rows={4}
                maxLength={300}
              />
              {errors.description && <div className="error-message">{errors.description}</div>}
              <small style={{ color: '#666', fontSize: '0.8rem' }}>
                {foodItem.description.length}/300 characters
              </small>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="price">Price (£) *</label>
                <input
                  id="price"
                  type="number"
                  step="0.01"
                  min="0"
                  value={foodItem.price}
                  onChange={(e) => handleInputChange('price', e.target.value)}
                  className={errors.price ? 'error' : ''}
                  placeholder="199.99"
                />
                {errors.price && <div className="error-message">{errors.price}</div>}
              </div>

              <div className="form-group">
                <label htmlFor="category">Category *</label>
                <select
                  id="category"
                  value={foodItem.category}
                  onChange={(e) => handleInputChange('category', e.target.value)}
                  className={errors.category ? 'error' : ''}
                >
                  <option value="">Select category</option>
                  {categories.map(cat => (
                    <option key={cat.value} value={cat.value}>
                      {cat.label}
                    </option>
                  ))}
                </select>
                {errors.category && <div className="error-message">{errors.category}</div>}
              </div>
            </div>
          </div>

          {/* Contact & Pickup Information */}
          <div className="form-section">
            <h3>Contact & Pickup Information</h3>

            <div className="form-group">
              <label htmlFor="phone_number">Phone Number *</label>
              <input
                id="phone_number"
                type="tel"
                value={foodItem.phone_number}
                onChange={(e) => handleInputChange('phone_number', e.target.value)}
                className={errors.phone_number ? 'error' : ''}
                placeholder="e.g., 07123 456789 or +44 7123 456789"
                maxLength={20}
              />
              {errors.phone_number && <div className="error-message">{errors.phone_number}</div>}
              <small style={{ color: '#666', fontSize: '0.8rem' }}>
                Enter UK phone number (e.g., 07123 456789 or +44 7123 456789)
              </small>
            </div>

            <div className="form-group">
              <div className="form-group-label-row">
                <label htmlFor="pickup_location">Pickup Location *</label>
                <button
                  type="button"
                  className="sfm-detect-btn"
                  onClick={handleDetectPickupLocation}
                  disabled={detectingLocation}
                >
                  {detectingLocation
                    ? <><Loader size={12} className="sfm-detect-spinner" /> Detecting...</>
                    : <><Navigation size={12} /> Detect location</>
                  }
                </button>
              </div>
              <textarea
                id="pickup_location"
                value={foodItem.pickup_location}
                onChange={(e) => handleInputChange('pickup_location', e.target.value)}
                className={errors.pickup_location ? 'error' : ''}
                placeholder="Enter your pickup location (e.g., 14 Baker Street, Marylebone, London, NW1 6XE)"
                rows={3}
              />
              {errors.pickup_location && <div className="error-message">{errors.pickup_location}</div>}
            </div>
          </div>

          {/* Images */}
          <div className="form-section">
            <h3>Images *</h3>
            <p className="section-description">Upload 1-5 high-quality images (JPG, PNG, WebP - max 5MB each)</p>

            <div className="image-upload-area">
              <input
                type="file"
                id="images"
                multiple
                accept="image/jpeg,image/jpg,image/png,image/webp"
                onChange={handleImageUpload}
                className="hidden-input"
                disabled={previews.length >= 5}
              />
              <label
                htmlFor="images"
                className={`upload-button ${previews.length >= 5 ? 'disabled' : ''}`}
              >
                <Upload size={20} />
                {previews.length >= 5 ? 'Maximum 5 images' : 'Upload Images'}
              </label>
            </div>

            {previews.length > 0 && (
              <div className="image-preview-grid">
                {previews.map((item, index) => (
                  <div key={item.url} className="image-preview">
                    <img src={item.url} alt={`Preview ${index + 1}`} />
                    <button
                      type="button"
                      className="remove-image"
                      onClick={() => removeImage(index)}
                    >
                      <X size={16} />
                    </button>
                  </div>
                ))}
              </div>
            )}
            {errors.images && <div className="error-message">{errors.images}</div>}
            <small style={{ color: '#666', fontSize: '0.8rem' }}>
              {previews.length}/5 images uploaded
            </small>
          </div>

          {/* Additional Details */}
          <div className="form-section">
            <h3>Additional Details</h3>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="serving_size">Serving Size *</label>
                <input
                  id="serving_size"
                  type="text"
                  value={foodItem.serving_size}
                  onChange={(e) => handleInputChange('serving_size', e.target.value)}
                  className={errors.serving_size ? 'error' : ''}
                  placeholder="2 persons"
                />
                {errors.serving_size && <div className="error-message">{errors.serving_size}</div>}
              </div>

              <div className="form-group">
                <label htmlFor="preparation_time">Preparation Time *</label>
                <input
                  id="preparation_time"
                  type="text"
                  value={foodItem.preparation_time}
                  onChange={(e) => handleInputChange('preparation_time', e.target.value)}
                  className={errors.preparation_time ? 'error' : ''}
                  placeholder="45 mins"
                />
                {errors.preparation_time && <div className="error-message">{errors.preparation_time}</div>}
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="available_quantity">Available Quantity *</label>
              <input
                id="available_quantity"
                type="number"
                min="1"
                value={foodItem.available_quantity}
                onChange={(e) => handleInputChange('available_quantity', e.target.value)}
                className={errors.available_quantity ? 'error' : ''}
                placeholder="10"
              />
              {errors.available_quantity && <div className="error-message">{errors.available_quantity}</div>}
              <small style={{ color: '#666', fontSize: '0.8rem' }}>
                How many portions are available for order?
              </small>
            </div>
          </div>

          {errors.general && <div className="error-message general-error">{errors.general}</div>}

          <div className="form-actions">
            <button type="button" onClick={() => { clearAll(); onClose(); }} className="cancel-btn">
              Cancel
            </button>
            <button type="submit" disabled={isSubmitting} className="submit-btn">
              {isSubmitting ? 'Creating...' : 'Create Food Item'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SellFoodModal;
